import React from 'react';
import PropTypes from 'prop-types';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import fr from 'facet-rest';
import useUserContext from 'Context/UserContext';

const AssumptionsAdminContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

const ASSUMPTION_REFRESH_STARTED = 'assumption-started';
const ASSUMPTION_REFRESH_SUCCEEDED = 'assumption-succeeded';
const ASSUMPTION_REFRESH_FAILED = 'assumption-refresh-failed';

const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    assumptionCollection: null,
    isAssumptionRefreshing: false,
    assumptionRefreshError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        assumptionCollection: action.assumptionCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case ASSUMPTION_REFRESH_STARTED: {
      return {
        ...state,
        isAssumptionRefreshing: true,
        assumptionRefreshError: null,
      };
    }
    case ASSUMPTION_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isAssumptionRefreshing: false,
        assumptionRefreshError: null,
        assumptionCollection: action.assumptionCollection,
      };
    }
    case ASSUMPTION_REFRESH_FAILED: {
      return {
        ...state,
        isAssumptionRefreshing: false,
        assumptionRefreshError: action.assumptionRefreshError,
      };
    }
    default: {
      throw new Error(`Unhandled AssumptionsAdminContext action type: ${action.type}`);
    }
  }
};

const AssumptionsAdminProvider = ({
  children,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);
  const { userResource } = useUserContext();

  const refreshAssumptionCollection = React.useCallback(async () => {
    const { assumptionCollection: assumptionColl } = state;

    if (assumptionColl === null) {
      return;
    }

    if (assumptionColl.resources().length() === 0) {
      return;
    }

    const [assumptionRes] = assumptionColl;

    try {
      dispatch({ type: ASSUMPTION_REFRESH_STARTED });

      const coll = await assumptionRes.link('self').get();

      dispatch({ type: ASSUMPTION_REFRESH_SUCCEEDED, assumptionCollection: coll });
    } catch (err) {
      dispatch({ type: ASSUMPTION_REFRESH_FAILED, assumptionRefreshError: err });
    }
  }, [state]);

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      assumptionCollection: state.assumptionCollection,
      isAssumptionRefreshing: state.isAssumptionRefreshing,
      assumptionRefreshError: state.assumptionRefreshError,
      refreshAssumptionCollection,
    };
  }, [
    state,
    refreshAssumptionCollection,
  ]);

  const loadAssumptions = async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });

      const resp = await userResource.relationship(fr.relationships.facetPlanAssumptions.name).link('related').get();

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        assumptionCollection: resp,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_STARTED, initializationError: err });
    }
  };

  useComponentDidMount(loadAssumptions);

  return (
    <AssumptionsAdminContext.Provider value={memoizedValue}>
      {children}
    </AssumptionsAdminContext.Provider>
  );
};

// Use AssumptionsAdmin State Context
const useAssumptionsAdminContext = () => {
  const context = React.useContext(AssumptionsAdminContext);
  if (context === undefined) {
    throw new Error('useAssumptionsAdminContext must be used within a AssumptionsAdminProvider');
  }
  return context;
};

AssumptionsAdminProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  AssumptionsAdminProvider,
  useAssumptionsAdminContext,
};
